<script setup>
/**
 * Personal profile settings.
 *
 * Intercom's grammar for the personal page: stacked section cards in the
 * narrow column - your details, interface preferences, signature, the send
 * shortcut, then the security block (password, MFA, sessions) and the access
 * token last. Each card keeps its own save action because these forms hit
 * different endpoints; there is no page-level dirty state to drive the
 * header's Save button.
 *
 * MFA and sessions render `flush` because their components bring their own
 * card containers; the MFA wizard itself stays on its standalone route.
 */
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useFontSize } from 'dashboard/composables/useFontSize';
import { useBranding } from 'shared/composables/useBranding';
import { clearCookiesOnLogout } from 'dashboard/store/utils/api.js';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import { parseBoolean } from '@chatwoot/utils';

import SettingsPageShell from 'dashboard/components-next/Settings/SettingsPageShell.vue';
import SettingsSection from 'dashboard/components-next/Settings/SettingsSection.vue';
import UserProfilePicture from './UserProfilePicture.vue';
import UserBasicDetails from './UserBasicDetails.vue';
import MessageSignature from './MessageSignature.vue';
import FontSize from './FontSize.vue';
import UserLanguageSelect from './UserLanguageSelect.vue';
import ChangePassword from './ChangePassword.vue';
import AccessToken from './AccessToken.vue';
import MfaSettingsCard from './MfaSettingsCard.vue';
import ActiveSessions from './ActiveSessions.vue';
import RadioCard from 'dashboard/components-next/radioCard/RadioCard.vue';

const { t } = useI18n();
const store = useStore();
const { isEditorHotKeyEnabled, updateUISettings } = useUISettings();
const { currentFontSize, updateFontSize } = useFontSize();
const { replaceInstallationName } = useBranding();

const currentUser = useMapGetter('getCurrentUser');
const globalConfig = useMapGetter('globalConfig/get');
const isOnChatwootCloud = useMapGetter('globalConfig/isOnChatwootCloud');

const avatarFile = ref('');
const avatarUrl = ref('');
const name = ref('');
const displayName = ref('');
const email = ref('');
const messageSignature = ref('');

const hotKeys = [
  {
    key: 'enter',
    title: t('PROFILE_SETTINGS.FORM.SEND_MESSAGE.CARD.ENTER_KEY.HEADING'),
    description: t('PROFILE_SETTINGS.FORM.SEND_MESSAGE.CARD.ENTER_KEY.CONTENT'),
    lightImage: '/assets/images/dashboard/profile/hot-key-enter.svg',
    darkImage: '/assets/images/dashboard/profile/hot-key-enter-dark.svg',
  },
  {
    key: 'cmd_enter',
    title: t('PROFILE_SETTINGS.FORM.SEND_MESSAGE.CARD.CMD_ENTER_KEY.HEADING'),
    description: t(
      'PROFILE_SETTINGS.FORM.SEND_MESSAGE.CARD.CMD_ENTER_KEY.CONTENT'
    ),
    lightImage: '/assets/images/dashboard/profile/hot-key-ctrl-enter.svg',
    darkImage: '/assets/images/dashboard/profile/hot-key-ctrl-enter-dark.svg',
  },
];

const apiAndWebhooksEnabled = computed(() => {
  if (!isOnChatwootCloud.value) return true;

  return currentUser.value.accounts.some(account => account.api_and_webhooks);
});

const accessTokenDescription = computed(() => {
  if (!apiAndWebhooksEnabled.value) {
    return t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.PAID_PLAN_NOTE');
  }

  return replaceInstallationName(t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.NOTE'));
});

const isMfaEnabled = computed(() =>
  parseBoolean(window.tolaConfig?.isMfaEnabled)
);

watch(
  currentUser,
  user => {
    if (!user?.id) return;

    name.value = user.name;
    email.value = user.email;
    avatarUrl.value = user.avatar_url;
    displayName.value = user.display_name;
    messageSignature.value = user.message_signature;
  },
  { immediate: true }
);

const dispatchUpdate = async (payload, successMessage, errorMessage) => {
  let alertMessage = '';
  try {
    await store.dispatch('updateProfile', payload);
    alertMessage = successMessage;

    return true; // return the value so that the status can be known
  } catch (error) {
    alertMessage = parseAPIErrorResponse(error) || errorMessage;

    return false; // return the value so that the status can be known
  } finally {
    useAlert(alertMessage);
  }
};

const updateProfile = async userAttributes => {
  const hasEmailChanged = currentUser.value.email !== userAttributes.email;
  name.value = userAttributes.name || name.value;
  email.value = userAttributes.email || email.value;
  displayName.value = userAttributes.displayName || displayName.value;

  const updatePayload = {
    name: name.value,
    email: email.value,
    displayName: displayName.value,
    avatar: avatarFile.value,
  };

  const success = await dispatchUpdate(
    updatePayload,
    hasEmailChanged
      ? t('PROFILE_SETTINGS.AFTER_EMAIL_CHANGED')
      : t('PROFILE_SETTINGS.UPDATE_SUCCESS'),
    t('RESET_PASSWORD.API.ERROR_MESSAGE')
  );

  if (hasEmailChanged && success) clearCookiesOnLogout();
};

const updateSignature = async signature => {
  await dispatchUpdate(
    { message_signature: signature },
    t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.API_SUCCESS'),
    t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.API_ERROR')
  );
};

const updateProfilePicture = ({ file, url }) => {
  avatarFile.value = file;
  avatarUrl.value = url;
};

const deleteProfilePicture = async () => {
  try {
    await store.dispatch('deleteAvatar');
    avatarUrl.value = '';
    avatarFile.value = '';
    useAlert(t('PROFILE_SETTINGS.AVATAR_DELETE_SUCCESS'));
  } catch (error) {
    useAlert(t('PROFILE_SETTINGS.AVATAR_DELETE_FAILED'));
  }
};

const toggleHotKey = key => {
  updateUISettings({ editor_message_key: key });
  useAlert(t('PROFILE_SETTINGS.FORM.SEND_MESSAGE.UPDATE_SUCCESS'));
};

const onCopyToken = async value => {
  if (!apiAndWebhooksEnabled.value) return;

  await copyTextToClipboard(value);
  useAlert(t('COMPONENTS.CODE.COPY_SUCCESSFUL'));
};

const resetAccessToken = async () => {
  if (!apiAndWebhooksEnabled.value) return;

  const success = await store.dispatch('resetAccessToken');
  if (success) {
    useAlert(t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.RESET_SUCCESS'));
  } else {
    useAlert(t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.RESET_ERROR'));
  }
};
</script>

<template>
  <SettingsPageShell :title="$t('PROFILE_SETTINGS.TITLE')">
    <SettingsSection
      :title="$t('PROFILE_SETTINGS.FORM.PROFILE_SECTION.TITLE')"
      :description="$t('PROFILE_SETTINGS.FORM.PROFILE_SECTION.NOTE')"
    >
      <div class="flex flex-col gap-6 p-4">
        <UserProfilePicture
          :src="avatarUrl"
          :name="name"
          @change="updateProfilePicture"
          @delete="deleteProfilePicture"
        />
        <UserBasicDetails
          :name="name"
          :display-name="displayName"
          :email="email"
          :email-enabled="!globalConfig.disableUserProfileUpdate"
          @update-user="updateProfile"
        />
      </div>
    </SettingsSection>

    <SettingsSection
      :title="$t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.TITLE')"
      :description="
        replaceInstallationName(
          $t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.NOTE')
        )
      "
    >
      <div class="flex flex-col items-start gap-6 p-4">
        <FontSize
          :value="currentFontSize"
          :label="$t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.FONT_SIZE.TITLE')"
          :description="
            $t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.FONT_SIZE.NOTE')
          "
          @change="updateFontSize"
        />
        <UserLanguageSelect
          :label="$t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.LANGUAGE.TITLE')"
          :description="
            $t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.LANGUAGE.NOTE')
          "
        />
      </div>
    </SettingsSection>

    <SettingsSection
      :title="$t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.TITLE')"
      :description="$t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.NOTE')"
    >
      <div class="p-4">
        <MessageSignature
          :message-signature="messageSignature"
          @update-signature="updateSignature"
        />
      </div>
    </SettingsSection>

    <SettingsSection
      :title="$t('PROFILE_SETTINGS.FORM.SEND_MESSAGE.TITLE')"
      :description="$t('PROFILE_SETTINGS.FORM.SEND_MESSAGE.NOTE')"
    >
      <div class="flex flex-col justify-between gap-5 p-4 sm:gap-4 sm:flex-row">
        <RadioCard
          v-for="hotKey in hotKeys"
          :id="hotKey.key"
          :key="hotKey.key"
          :label="hotKey.title"
          :description="hotKey.description"
          :is-active="isEditorHotKeyEnabled(hotKey.key)"
          class="sm:flex-1"
          @select="toggleHotKey"
        >
          <img
            :src="hotKey.lightImage"
            :alt="`Light themed image for ${hotKey.title}`"
            class="block object-cover w-full dark:hidden"
          />
          <img
            :src="hotKey.darkImage"
            :alt="`Dark themed image for ${hotKey.title}`"
            class="hidden object-cover w-full dark:block"
          />
        </RadioCard>
      </div>
    </SettingsSection>

    <SettingsSection
      v-if="!globalConfig.disableUserProfileUpdate"
      :title="$t('PROFILE_SETTINGS.FORM.PASSWORD_SECTION.TITLE')"
    >
      <div class="p-4">
        <ChangePassword />
      </div>
    </SettingsSection>

    <SettingsSection
      v-if="isMfaEnabled"
      flush
      :title="$t('PROFILE_SETTINGS.FORM.SECURITY_SECTION.TITLE')"
      :description="$t('PROFILE_SETTINGS.FORM.SECURITY_SECTION.NOTE')"
    >
      <MfaSettingsCard />
    </SettingsSection>

    <SettingsSection
      flush
      :title="$t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.TITLE')"
      :description="$t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.NOTE')"
    >
      <ActiveSessions />
    </SettingsSection>

    <SettingsSection
      :title="$t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.TITLE')"
      :description="accessTokenDescription"
    >
      <div class="p-4">
        <AccessToken
          :value="currentUser.access_token"
          :disabled="!apiAndWebhooksEnabled"
          @on-copy="onCopyToken"
          @on-reset="resetAccessToken"
        />
      </div>
    </SettingsSection>
  </SettingsPageShell>
</template>
